import {
  colorBaseNeutral700,
  colorBaseNeutralAlt300,
  colorBaseNeutralBlack,
  colorBaseNeutralWhite,
  colorBrandSecondaryDefault,
  colorContentDim,
  colorContentOnBrandDefault,
} from '@duxton-web/design-tokens/themes/light';
import {
  CheckmarkUniversal,
  MoreHorizontalUniversal,
  SendUniversal,
} from '@duxton-web/icons';
import {
  Button,
  Card,
  Dropdown,
  Flex,
  Form,
  Tag,
  Typography,
} from '@duxton-web/ui';
import React, { useMemo } from 'react';

import logo from 'img/logo.svg';
import smartCampaignPreviewBg from 'img/smart_campaign_preview_bg.png';
import _ from 'lodash';
import { useCreateSmartCampaign } from 'omega-rtc/components/container/CreateSmartCampaignProvider';
import { SMART_CAMPAIGN_FORM_KEYS } from 'omega-rtc/constants/ucd';
import supportedLanguages from 'omega-rtc/data/languages.json';
import { getLanguageNameFromCode } from 'omega-rtc/utils/smart-campaign-utils';
import { getVariantNumberFromSmartCommsVariantName } from 'omega-rtc/utils/ucd-utils';
import './SmartCampaignContentVariants.scss';

import { showControlGroup } from 'omega-rtc/config';
import SmartCampaignCustomInput from 'omega-rtc/components/form/ucd/SmartCampaignCustomInput';

interface NotificationCardProps {
  title: string;
  message: string;
}

interface InputBoxProps {
  minRow?: number;
  language: string;
  roundName: string;
  variantName: string;
  errorMessage: string;
  inputBoxTitle: string;
  primaryLanguage: string;
  inputBoxPlaceholder: string;
  type: | typeof SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_TITLE
    | typeof SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_MESSAGE;
}

interface SmartCampaignFormContentCardProps {
  slang?: string;
  language: string;
  roundName: string;
  isPreviewMode: boolean;
  variantName: string;
  isChosen: boolean;
  isControlledGroup: boolean;
  sentPreviewMessage: (params: {
    variantName: string;
    title: string;
    message: string;
  }) => void;
}

const SmartCampaignFormContentCard: React.FC<SmartCampaignFormContentCardProps> = ({
  slang,
  language,
  roundName,
  isChosen,
  isPreviewMode,
  variantName,
  sentPreviewMessage,
  isControlledGroup,
}) => {
  const {
    primaryLanguage,
    toggleControlledGroup,
    toggleContentSelection,
    isCurrentContentSourceManual,
  } = useCreateSmartCampaign();

  const baseNameOfTitleAndMessageInForm = useMemo(() => {
    return [
      SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT,
      roundName,
      language,
      variantName,
    ];
  }, [roundName, language, variantName]);

  const titleWatcher = Form.useWatch([
    ...baseNameOfTitleAndMessageInForm,
    SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_TITLE,
  ]);

  const messageWatcher = Form.useWatch([
    ...baseNameOfTitleAndMessageInForm,
    SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_MESSAGE,
  ]);

  const dropDownMenuProps = {
    items: [
      {
        label: 'Send preview message',
        key: 'send-preview',
        icon: <SendUniversal style={{ fontSize: '20px' }} />,
      },
    ],
    onClick: (e) => {
      switch (e.key) {
        case 'send-preview':
          sentPreviewMessage({
            variantName,
            message: messageWatcher,
            title: titleWatcher,
          });
          break;
        default:
          break;
      }
    },
  };

  return (
    <Card
      key={variantName}
      size="default"
      style={{ textShadow: 'none' }}
      styles={{
        body: {
          display: 'flex',
          paddingTop: 16,
          paddingLeft: 20,
          paddingRight: 20,
          paddingBottom: 24,
          flexDirection: 'column',
        },
      }}
    >
      <React.Fragment>
        <Flex
          align="center"
          justify="space-between"
          style={{
            marginBottom: 8,
          }}
        >
          <Flex align="center" justify="flex-start" gap={8}>
            <Typography.Body
              level={3}
              style={{ color: colorBaseNeutral700 }}
            >{`Variant ${getVariantNumberFromSmartCommsVariantName(variantName)}`}</Typography.Body>
            {
              <Dropdown menu={dropDownMenuProps} trigger={['click']}>
                <MoreHorizontalUniversal />
              </Dropdown>
            }
          </Flex>
          <Flex gap={8}>
            {showControlGroup && (
              <Button
                style={{
                  display: primaryLanguage === language ? 'flex' : 'none',
                  ...(isControlledGroup && {
                    backgroundColor: colorBrandSecondaryDefault,
                    color: colorContentOnBrandDefault,
                  }),
                }}
                size="small"
                type="secondary"
                disabled={isChosen}
                icon={<CheckmarkUniversal />}
                onClick={() => toggleControlledGroup(variantName)}
                styles={{
                  icon: {
                    display: isControlledGroup ? 'flex' : 'none',
                  },
                }}
              >
                {isControlledGroup ? 'Controlled' : 'Mark as control'}
              </Button>
            )}

            <Button
              type="secondary"
              size="small"
              disabled={isControlledGroup}
              style={{
                display: primaryLanguage === language ? 'flex' : 'none',
                ...(isChosen && {
                  backgroundColor: colorBrandSecondaryDefault,
                  color: colorContentOnBrandDefault,
                }),
              }}
              styles={{
                icon: {
                  display: isChosen || isControlledGroup ? 'flex' : 'none',
                },
              }}
              icon={<CheckmarkUniversal />}
              onClick={() => toggleContentSelection(variantName, roundName)}
            >
              {isChosen || isControlledGroup ? 'Selected' : 'Select'}
            </Button>
          </Flex>
        </Flex>

        <div className="variant-body-with-input-box-and-preview">
          {isPreviewMode && (
            <div
              className="variant-preview"
              style={{
                padding: 20,
                maxWidth: 375,
                minHeight: 290,
                display: 'flex',
                alignItems: 'center',
                backgroundSize: 'cover',
                justifyContent: 'center',
                borderBottomRightRadius: 24,
                borderBottomLeftRadius: 24,
                backgroundImage: `url(${smartCampaignPreviewBg})`,
              }}
            >
              <NotificationCard title={titleWatcher} message={messageWatcher} />
            </div>
          )}
          <Flex vertical gap={20} style={{ width: '100%' }}>
            <InputBox
              language={language}
              roundName={roundName}
              inputBoxTitle={'Title'}
              variantName={variantName}
              primaryLanguage={primaryLanguage}
              inputBoxPlaceholder={`Enter ${supportedLanguages[language].label} Title`}
              errorMessage={
                isCurrentContentSourceManual ? '' : 'Please enter title'
              }
              type={SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_TITLE}
              key={SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_TITLE}
            />
            <InputBox
              minRow={5}
              language={language}
              roundName={roundName}
              inputBoxTitle={'Message'}
              variantName={variantName}
              primaryLanguage={primaryLanguage}
              inputBoxPlaceholder={`Enter ${supportedLanguages[language].label} Message`}
              errorMessage={
                isCurrentContentSourceManual ? '' : 'Please enter message'
              }
              type={SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_MESSAGE}
              key={SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_MESSAGE}
            />
            {primaryLanguage === language && !isCurrentContentSourceManual && (
              <Tag color="blue" style={{ width: 'fit-content' }}>
                {_.capitalize(
                  slang || getLanguageNameFromCode({ languageCode: language }), // Value of slang will be undefined, if this variant is not generated using slang. if so, we will show the language name.
                )}
              </Tag>
            )}
          </Flex>
        </div>
      </React.Fragment>
    </Card>
  );
};

const InputBox: React.FC<InputBoxProps> = ({
  type,
  minRow,
  language,
  roundName,
  variantName,
  errorMessage,
  inputBoxTitle,
  primaryLanguage,
  inputBoxPlaceholder,
}) => {
  const formPath = [
    SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT,
    roundName,
    language,
    variantName,
    type,
  ];

  return (
    <Flex gap={5} vertical>
      <Typography.Body level={2} style={{ color: colorBaseNeutral700 }}>
        {inputBoxTitle}
      </Typography.Body>
      <Form.Item
        style={{ marginBottom: 4 }}
        name={formPath}
        rules={[{ message: errorMessage, required: true }]}
      >
        <SmartCampaignCustomInput
          minRows={minRow || 2}
          errorMessage={errorMessage}
          placeholder={inputBoxPlaceholder}
          path={formPath}
        />
      </Form.Item>
      {primaryLanguage !== language && (
        <HelperTextForNonPrimaryLanguage
          type={type}
          roundName={roundName}
          variantName={variantName}
          primaryLanguage={primaryLanguage}
        />
      )}
    </Flex>
  );
};

const HelperTextForNonPrimaryLanguage: React.FC<{
  primaryLanguage: string;
  roundName: string;
  variantName: string;
  type: typeof SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_TITLE | typeof SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_MESSAGE;
}> = ({
  primaryLanguage,
  roundName,
  variantName,
  type,
}) => {
  const formPathBasedBasedOnType = [
    SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT,
    roundName,
    primaryLanguage,
    variantName,
    type,
  ];
  const watchContent = Form.useWatch(formPathBasedBasedOnType);

  return (
    <Typography.Metadata level={1} style={{ color: colorContentDim }}>
      {watchContent}
    </Typography.Metadata>
  );
};

const NotificationCard: React.FC<NotificationCardProps> = ({ title, message }) => {
  if (!title && !message) {
    return <React.Fragment />;
  }

  return (
    <Flex
      vertical
      gap={5}
      style={{
        width: '100%',
        padding: 10,
        borderRadius: 12,
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
      }}
    >
      <Flex justify="space-between" align="center">
        <Flex gap={10} align="center">
          <Flex
            style={{
              backgroundColor: colorBaseNeutralWhite,
              padding: 2,
              borderRadius: 7,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={logo}
              alt="logo"
              width={20}
              height={20}
              style={{ objectFit: 'contain' }}
            />
          </Flex>

          <Typography.Metadata
            level={1}
            style={{ color: colorBaseNeutralAlt300, fontWeight: 500 }}
          >
            GRAB
          </Typography.Metadata>
        </Flex>
        <Typography.Metadata
          level={1}
          style={{ color: colorBaseNeutralAlt300, fontWeight: 500 }}
        >
          now
        </Typography.Metadata>
      </Flex>

      <Typography.Body
        level={3}
        style={{
          fontWeight: 500,
          whiteSpace: 'pre-wrap',
          color: colorBaseNeutralBlack,
        }}
      >
        {title}
      </Typography.Body>
      <Typography.Body
        level={3}
        style={{ color: colorBaseNeutralBlack, whiteSpace: 'pre-wrap' }}
      >
        {message}
      </Typography.Body>
    </Flex>
  );
};

export default SmartCampaignFormContentCard;
